import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbScriptTag } from "@/lib/breadcrumbSchema";
import { SCHEMA_LOCALBUSINESS_ID, SITE_URL } from "@/lib/entitySchema";

const TIER_1 = [
  { name: "Colorado Springs", to: "/colorado-springs", note: "HQ — Briargate, Northgate, Powers, Old Colorado City, plus Manitou Springs, Black Forest, Falcon, Peyton, Cimarron Hills, Security-Widefield, Fountain, and Stratmoor handled inside this hub." },
  { name: "Monument", to: "/monument", note: "Tri-Lakes — Monument, Palmer Lake, Woodmoor, Gleneagle. 15 minutes north of our office." },
  { name: "Castle Rock", to: "/castle-rock", note: "Douglas County — Founders, The Meadows, Crystal Valley, Castle Pines. 45 minutes north." },
  { name: "Parker", to: "/parker", note: "South Denver-metro — Stroh Ranch, Pinery, Stonegate, Mainstreet Parker. 1 hour north." },
];

const SECONDARY = [
  "Falcon", "Peyton", "Woodland Park", "Black Forest", "Fountain",
  "Security-Widefield", "Manitou Springs", "Pueblo",
];

const MENTION_ONLY = [
  "Denver", "Boulder", "Fort Collins", "Aurora", "Arvada", "Lakewood",
  "Westminster", "Thornton", "Broomfield", "Centennial", "Highlands Ranch",
  "Littleton", "Longmont", "Loveland",
];

export const Route = createFileRoute("/colorado-coverage")({
  head: () => ({
    meta: [
        { name: "description", content: "Statewide Colorado coverage from our Colorado Springs HQ. Web design, SEO, and digital marketing in every major Front Range and Western Slope city." },
      { title: "Colorado Service Areas — Colorado Web Impressions" },
      { name: "description", content: "Where we work, where we don't, and why. Headquartered in Colorado Springs, primary coverage across El Paso County and south Denver metro (Monument, Castle Rock, Parker)." },
      { property: "og:title", content: "Colorado Service Areas — Colorado Web Impressions" },
      { property: "og:description", content: "Statewide Colorado coverage from our Colorado Springs HQ. Web design, SEO, and digital marketing in every major Front Range and Western Slope city." },
      { property: "og:url", content: "https://coloradowebimpressions.com/colorado-coverage" },
    
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Colorado Service Areas — Colorado Web Impressions" },
      { name: "twitter:description", content: "Where we work, where we don't, and why. Headquartered in Colorado Springs, primary coverage across El Paso County and south Denver metro (Monument, Castle Rock, Parker)." },
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/colorado-coverage" }],
    scripts: [
      breadcrumbScriptTag([
        { name: "Home", path: "/" },
        { name: "Colorado Coverage" },
      ]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/colorado-coverage#collection`,
          url: `${SITE_URL}/colorado-coverage`,
          name: "Colorado Service Areas — Colorado Web Impressions",
          description:
            "Primary, secondary, and mention-only Colorado cities served by Colorado Web Impressions.",
          isPartOf: { "@id": `${SITE_URL}/#website` },
          about: { "@id": SCHEMA_LOCALBUSINESS_ID },
          mainEntity: {
            "@type": "ItemList",
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            numberOfItems: TIER_1.length,
            itemListElement: TIER_1.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${SITE_URL}${c.to}`,
              name: `${c.name}, Colorado`,
            })),
          },
        }),
      },
    ],
  }),
  component: ColoradoCoveragePage,
});

function ColoradoCoveragePage() {
  return (
    <main className="container mx-auto max-w-3xl px-6 py-16">
      <header className="mb-12">
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">Service areas</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight">Where we work in Colorado — and where we don't</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Headquartered on E Woodmen in Colorado Springs. We build deep, hands-on
          relationships with businesses we can actually meet in person — which
          means our work is concentrated in El Paso County and the south Denver
          metro corridor. Honest answer instead of pretending to serve everyone.
        </p>
      </header>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold">Primary markets</h2>
        <p className="mt-2 text-muted-foreground">
          We build dedicated pages for these cities because we have the proof,
          the drive time, and the local knowledge to win them.
        </p>
        <ul className="mt-6 space-y-4">
          {TIER_1.map((c) => (
            <li key={c.to} className="rounded-lg border border-border p-5">
              <Link to={c.to} className="text-lg font-semibold text-primary hover:underline">
                {c.name}
              </Link>
              <p className="mt-1 text-sm text-muted-foreground">{c.note}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold">Secondary markets — we'll travel</h2>
        <p className="mt-2 text-muted-foreground">
          We work with clients in these communities, but we don't pretend to
          dominate them. If you're here and serious, get in touch.
        </p>
        <p className="mt-4 text-base">{SECONDARY.join(" · ")}</p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold">Front Range &amp; Denver metro</h2>
        <p className="mt-2 text-muted-foreground">
          We've worked with clients across the broader Front Range, but we won't
          pretend a Colorado Springs agency can out-rank a Denver-based shop
          inside Denver's own market. If you're a metro-Denver business looking
          for an agency that lives there, we'll be straight with you about that.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">{MENTION_ONLY.join(" · ")}</p>
      </section>

      <section className="rounded-lg bg-card p-6">
        <h2 className="text-xl font-semibold">Not sure if we're a fit?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The fastest way to find out is a 15-minute call.
        </p>
        <Link
          to="/contact"
          className="mt-4 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          Talk to us
        </Link>
      </section>
    </main>
  );
}
